import { useMemo } from 'react'

interface Params {
  campaignsOrDeals: 'deals' | 'campaigns' | 'none'
  flightDeals: Array<App.FlightDeal>
  /** The currently active airport from FlightDealsCarousel (selectedAirport ?? defaultAirport) */
  dealAirport: App.Airport | undefined
}

const EMPTY_SET = new Set<string>()

/**
 * Returns a Set of destination airport codes (uppercased) that are already
 * shown by either FlightDealsCarousel or FlightCampaigns, so that
 * PopularFlightsSection can exclude them to avoid duplicate destinations.
 *
 * - For deals mode: mirrors FlightDealsCarousel's origin-airport filter so
 *   only destinations visible in the carousel are excluded.
 * - For campaigns mode: campaigns don't carry destination airport codes and
 *   their deals aren't fetched on the landing page, so all flightDeals
 *   destinations are used as a best-effort proxy.
 */
export function useExcludedFlightDestinations(params: Params): Set<string> {
  const { campaignsOrDeals, flightDeals, dealAirport } = params

  return useMemo(() => {
    if (campaignsOrDeals === 'deals' && dealAirport) {
      // Match FlightDealsCarousel's internal filtering:
      // only deals for the selected origin airport are shown
      const airportDeals = flightDeals.filter(
        d => d.originAirportCode === dealAirport.code,
      )
      return new Set(airportDeals.map(d => d.destinationAirportCode.toUpperCase()))
    }

    if (campaignsOrDeals === 'campaigns') {
      // Campaigns don't have destinationAirportCode and campaign deals
      // aren't fetched on the landing page. Use all flightDeals destinations
      // as a proxy for what's being promoted.
      return new Set(flightDeals.map(d => d.destinationAirportCode.toUpperCase()))
    }

    return EMPTY_SET
  }, [campaignsOrDeals, flightDeals, dealAirport])
}
